import { handleMutation } from '../../../legacyCore/Form';
import { DILM_CLIENT_CONTEXT, LM_CLIENT_CONTEXT, OTHER_ID } from '../../constants';
import toISODateString from '../../utils/toISODateString';
import { DEPENDENT_STATUS } from './constants';

function createUser(values, mutations) {
  if (values.user) {
    return { isSuccessful: true, user: values.user };
  }
  return handleMutation(
    mutations.createUser({
      variables: {
        username: values.username,
        password: values.password,
        givenName: values.contactGivenName || values.patientGivenName,
        familyName: values.contactFamilyName || values.patientFamilyName,
        verificationToken: values.verificationToken,
      },
      context: LM_CLIENT_CONTEXT,
    }),
    'createUser'
  );
}

function createPatient(values, mutations) {
  const contactBirthDate =
    values.contactBirthYear && values.contactBirthDay && values.contactBirthMonth
      ? `${values.contactBirthYear}-${values.contactBirthMonth}-${values.contactBirthDay.padStart(
          2,
          '0'
        )}`
      : values.contactBirthDate;

  if (values.user && values.user.patient) {
    return { isSuccessful: true, patient: values.user.patient };
  }
  return handleMutation(
    mutations.createPatient({
      variables: {
        userId: values.user.id,
        givenName: values.contactGivenName || values.patientGivenName,
        familyName: values.contactFamilyName || values.patientFamilyName,
        birthDate: toISODateString(contactBirthDate || values.patientBirthDate),
        email: values.contactEmail,
        mobilePhone: values.contactMobilePhone,
        termsAgreedAt: new Date().toISOString(),
      },
      context: LM_CLIENT_CONTEXT,
    }),
    'createPatient'
  );
}

function createFavoriteProvider(values, mutations) {
  return handleMutation(
    mutations.createFavoriteProvider({
      variables: {
        patientId: values.user.patient.id,
        providerId: values.provider.id,
      },
      context: LM_CLIENT_CONTEXT,
    }),
    'createFavoriteProvider'
  );
}

function createDependent(values, mutations) {
  if (values && values.patient) {
    return { isSuccessful: true, patient: values.patient };
  }
  return handleMutation(
    mutations.createDependent({
      variables: {
        caregiverId: values.user.patient.id,
        givenName: values.patientGivenName,
        familyName: values.patientFamilyName,
        birthDate: toISODateString(values.patientBirthDate),
      },
      context: LM_CLIENT_CONTEXT,
    }),
    'createDependent'
  );
}

async function createAppointment(values, mutations) {
  const user = values.user || {};
  const patient = values.patient || {};
  const userPatient = user.patient || {};
  let { patientGivenName } = values;
  let { patientFamilyName } = values;
  let contactGivenName = values.contactGivenName || patientGivenName;
  let contactFamilyName = values.contactFamilyName || patientFamilyName;

  if (patient.display) {
    [patientGivenName, patientFamilyName] = patient.display.split(' ');
  }
  if (userPatient.display) {
    [contactGivenName, contactFamilyName] = userPatient.display.split(' ');
  }

  const variables = {
    openingId: values.opening.id,
    provider: {
      id: values.provider.id,
      display: values.provider.display,
    },
    reasonForVisit: {
      id: values.reasonForVisit.id,
      display: values.reasonForVisit.display,
    },
    userId: user.id,
    patientId: patient.id,
    patientName: `${patientGivenName} ${patientFamilyName}`,
    patientGivenName,
    patientFamilyName,
    partnerId: values.partnerId,
    widgetId: values.widgetId,
    contactGivenName,
    contactFamilyName,
    contactEmail: values.contactEmail,
    contactMobilePhone: values.contactMobilePhone,
    notes: values.notes,
    termsAgreedAt: new Date().toISOString(),
    verificationToken: values.verificationToken,
    source: values.source,
    channel: values.channel,
  };

  if (patient.birthDate || patient.birthdate) {
    variables.patientBirthDate = patient.birthDate || patient.birthdate;
  } else if (values.patientBirthYear && values.patientBirthDay && values.patientBirthMonth) {
    const date = `${values.patientBirthYear}-${
      values.patientBirthMonth
    }-${values.patientBirthDay.padStart(2, '0')}`;

    variables.patientBirthDate = toISODateString(date);
  } else {
    variables.patientBirthDate = toISODateString(values.patientBirthDate);
  }

  if (userPatient.display) {
    variables.contactName = userPatient.display;
  } else if (values.dependentStatus === DEPENDENT_STATUS.DEPENDENT) {
    variables.contactName = `${values.contactGivenName} ${values.contactFamilyName}`;
  } else {
    variables.contactName = variables.patientName;
  }

  if (userPatient.email) {
    variables.contactEmail = userPatient.email;
  } else {
    variables.contactEmail = values.contactEmail;
  }

  if (userPatient.mobilePhone) {
    variables.contactMobilePhone = userPatient.mobilePhone;
  } else {
    variables.contactMobilePhone = values.contactMobilePhone;
  }

  if (values.insuranceIssuer != null && values.insuranceIssuer.id !== OTHER_ID) {
    const firstCoverage = {
      insuranceIssuer: {
        id: values.insuranceIssuer.id,
        display: values.insuranceIssuer.display,
      },
    };
    if (values.memberId) {
      firstCoverage.insurancePhone = values.insurancePhone;
      firstCoverage.memberId = values.memberId;
      firstCoverage.group = values.group;
      firstCoverage.employer = { display: values.employer };
      firstCoverage.subscriberGivenName = values.subscriberGivenName;
      firstCoverage.subscriberFamilyName = values.subscriberFamilyName;
      firstCoverage.subscriberDateOfBirth = toISODateString(values.subscriberBirthDate);
      firstCoverage.postalCode = values.subscriberPostalCode;
      firstCoverage.ssn = values.subscriberSsn;
    }
    variables.patientCoverage = [firstCoverage];
  } else {
    variables.patientCoverage = [];
  }

  return handleMutation(
    mutations.createAppointment({ variables, context: LM_CLIENT_CONTEXT }),
    'createAppointment'
  );
}

function widgetSessionScheduled({ appointment, widgetId }, mutations) {
  return mutations
    .widgetSessionScheduled({
      variables: {
        lmAppointmentId: appointment.id,
        widgetId,
      },
      context: DILM_CLIENT_CONTEXT,
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn(error);
    });
}

function markScheduleRequestAttemptAsScheduled(
  { appointment, scheduleRequestAttemptData },
  mutations
) {
  return handleMutation(
    mutations.markScheduleRequestAttemptAsScheduled({
      variables: {
        lmAppointmentId: appointment.id,
        scheduleRequestAttemptId: scheduleRequestAttemptData.id,
        token: scheduleRequestAttemptData.token,
      },
      context: DILM_CLIENT_CONTEXT,
    }),
    'markScheduleRequestAttemptAsScheduled'
  ).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn(error);
  });
}

export default async function submitAppointment(values, mutations) {
  let status = {};

  if (values.createAccount) {
    status = await createUser(values, mutations);
    if (!status.isSuccessful) {
      return status;
    }
    values = { ...values, user: status.user };
  }

  if (values.user) {
    const createPatientStatus = await createPatient(values, mutations);
    if (!createPatientStatus.isSuccessful) {
      return { ...status, ...createPatientStatus };
    }
    const userWithPatient = { ...values.user, patient: createPatientStatus.patient };
    status = { ...status, user: userWithPatient };
    values = { ...values, user: userWithPatient };

    const createFavoriteProviderStatus = await createFavoriteProvider(values, mutations);
    status = { ...status, ...createFavoriteProviderStatus };
    if (!status.isSuccessful) {
      return status;
    }

    if (values.createDependent || values.dependentStatus === DEPENDENT_STATUS.DEPENDENT) {
      const createDependentStatus = await createDependent(values, mutations);
      status = { ...status, ...createDependentStatus };
      if (!status.isSuccessful) {
        return status;
      }
      values = { ...values, patient: status.patient };
    } else {
      if (!values.patient) {
        values = { ...values, patient: values.user.patient };
      }
      status = { ...status, patient: values.patient };
    }
  }

  const createAppointmentStatus = await createAppointment(values, mutations);

  status = { ...status, ...createAppointmentStatus };
  if (!status.isSuccessful) return status;

  values = { ...values, appointment: createAppointmentStatus.appointment };
  if (values.widgetId) {
    widgetSessionScheduled(values, mutations);
  }
  if (values.scheduleRequestAttemptData) {
    markScheduleRequestAttemptAsScheduled(values, mutations);
  }
  return status;
}
